import { useMutation, useQuery } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import axios from "../../../api/axios";
import './adjustment.scss';
import logo from '../../../assets/msw-logo.png';
import Swal from "sweetalert2";

const Adjustment = () => {
    const search = window.location.search;
    const param = new URLSearchParams(search);
    const token = param.get('auth');

    const [error, setError] = useState(false);
    const [success, setSucccess] = useState(false);
    const [message, setMessage] = useState(null);
    const [updateRecord, setUpdateRecord] = useState(false);

    const [timeIn, setTimeIn] = useState(null);
    const [timeOut, setTimeOut] = useState(null);
    const [startBreak, setStartBreak] = useState(null);
    const [stopBreak, setStopBreak] = useState(null);

    const handleCancelRecordUpdate = () => {
        setTimeIn(null);
        setTimeOut(null);
        setStartBreak(null);
        setStopBreak(null);
        setUpdateRecord(false);
    }

    const { data: record, isLoading: isLoadingRecord, error: errorRecord } = useQuery({
        queryKey: ['adjustmentRecord', token],
        queryFn: async () => {
            const res = await axios.get(`dtr/adjustments/${token}`);
            return res.data.response;
        },
        enabled: !!token,
        retry: 1
    });

    useEffect(() => {
        if (record) {
            if (record?.dtrstatus === 2) {
                setError(true);
                setMessage(`Timesheet of ${record?.name} is already approved.`)
            } else if (record?.requeststatus > 0) {
                setError(true);
                setMessage(`Record Adjustment request of ${record?.name} is already ${record?.requeststatus === 1 ? 'APPROVED' : 'REJECTED'}`);
            }
        }
    }, [record]);

    useEffect(() => {
        if (errorRecord) {
            setError(true);
            setMessage(errorRecord.response?.data.response || 'Something went wrong while fetching adjustment record details!');
        }
    });

    const rejectAdjustmentRequestMutation = useMutation({
        mutationFn: async () => {
            const res = await axios.patch('dtr/adjustments', null, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            return res.data;
        },
        onSuccess: (data) => {
            setSucccess(true);
            setMessage(data?.response);
        },
        onError: (error) => {
            setError(true);
            setMessage(error.response?.data.response || 'Something went wrong while rejecting adjustment request!');
        }
    });

    const handleRejectAdjustmentRequest = () => {
        Swal.fire({
            text: 'Are you sure you want to REJECT this adjustment request?',
            icon: 'warning',
            allowOutsideClick: false,
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            showLoaderOnConfirm: true,
            preConfirm: async (result) => {
                if (result) {
                    await rejectAdjustmentRequestMutation.mutate();
                }
            }
        });
    }

    const saveRecordUpdateMutation = useMutation({
        mutationFn: async (data) => {
            const res = await axios.put('dtr/adjustments', data, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            return res.data;
        },
        onSuccess: (data) => {
            setSucccess(true);
            setMessage(data?.response);
        },
        onError: (error) => {
            setError(true);
            setMessage(error.response?.data.response || 'Something went wrong while rejecting adjustment request!');
        }
    });

    const handleSavingRecordUpdate = () => {
        Swal.fire({
            text: `Are you sure you want to APPROVE this adjustment request?`,
            icon: 'warning',
            allowOutsideClick: false,
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            showLoaderOnConfirm: true,
            preConfirm: async function(result) {
                if (result) {
                    await saveRecordUpdateMutation.mutate({
                        adjustmentid: record?.adjustmentid,
                        date: record?.rawdate,
                        dtrid: record?.dtrid,
                        timein: timeIn,
                        timeout: timeOut,
                        startbreak: startBreak,
                        stopbreak: stopBreak
                    });
                }
            }
        });
    }

    return (
        <div className={`nopriv adjustment ${isLoadingRecord ? 'loading' : ''}`}>
            {error
                ? (
                    <div className="error">
                        <div className="icon">
                            <i className="fa fa-exclamation-triangle"></i>
                        </div>
                        <p className="message">{message}</p>
                    </div>
                ) : (success 
                    ? (
                        <div className="success">
                            <div className="icon">
                                <i className="fa fa-check-circle"></i>
                            </div>
                            <p className="message">{message}</p>
                        </div>
                    ) : (
                        <div className="content">
                            <div className="header">
                                <h3>Record Adjustment</h3>
                                {!updateRecord && (
                                    <div className="buttons">
                                        <button type="buttons" className="btn-primary" title="UPDATE" onClick={() => setUpdateRecord(true)}>
                                            <i className="fa fa-check"></i>
                                        </button>
                                        <button type="button" className="btn-danger" title="DISAPPROVE" onClick={() => handleRejectAdjustmentRequest()}>
                                            <i className="fa fa-times"></i>
                                        </button>
                                    </div>
                                )}
                            </div>

                            <table>
                                <tbody>
                                    <tr>
                                        <th>Name</th>
                                        <td>
                                            <span><strong>{record?.name}</strong></span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Department</th>
                                        <td>
                                            <span><strong>{record?.department}</strong></span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Day</th>
                                        <td>
                                            <span><strong>{record?.day}</strong></span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Shift</th>
                                        <td>
                                            <span><strong>{record?.usershift}</strong></span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Time In</th>
                                        <td>
                                            <span>
                                                {updateRecord 
                                                    ? (
                                                        <input type="datetime-local" value={timeIn} min={record?.mindate + 'T00:00'} max={record?.maxdate + 'T11:59'} onChange={(e) => setTimeIn(e.target.value || record?.timein)} />
                                                    ) : (
                                                        record?.timein
                                                    )}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Time Out</th>
                                        <td>
                                            <span>
                                                {updateRecord 
                                                    ? (
                                                        <input type="datetime-local" value={timeOut} min={record?.mindate + 'T00:00'} max={record?.maxdate + 'T11:59'} onChange={(e) => setTimeOut(e.target.value || record?.timeout)} />
                                                    ) : (
                                                        record?.timeout
                                                    )}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Start Break</th>
                                        <td>
                                            <span>
                                                {updateRecord 
                                                    ? (
                                                        <input type="datetime-local" value={startBreak} min={record?.mindate + 'T00:00'} max={record?.maxdate + 'T11:59'} onChange={(e) => setStartBreak(e.target.value || record?.startbreak)} />
                                                    ) : (
                                                        record?.startbreak
                                                    )}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Stop Break</th>
                                        <td>
                                            <span>
                                                {updateRecord 
                                                    ? (
                                                        <input type="datetime-local" value={stopBreak} min={record?.mindate + 'T00:00'} max={record?.maxdate + 'T11:59'} onChange={(e) => setStopBreak(e.target.value || record?.stopbreak)} />
                                                    ) : (
                                                        record?.stopbreak
                                                    )}
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            {updateRecord && (
                                <div className="buttons">
                                    <button type="button" onClick={() => handleSavingRecordUpdate()}>
                                        SAVE
                                    </button>
                                    <button type="button" onClick={() => handleCancelRecordUpdate()}>
                                        CANCEL
                                    </button>
                                </div>
                            )}
                        </div>
                    )                    
                )}

            <div className="adjustment-footer">
                <div className="logo">
                    <img src={logo} alt="Megasportsworld" />
                </div>
                <p>&copy; Megasportsworld 2024.</p>
            </div>
        </div>
    )
}

export default Adjustment;